import React, { useCallback, useEffect, useState } from 'react';
import { startRegistration } from '@simplewebauthn/browser';
import { apiFetch, getApiHost } from '../utils/api';

type PasskeySummary = {
  credentialId: string;
  name: string | null;
  transports: string[];
  createdAt: string;
  lastUsedAt: string | null;
};

/**
 * Profile-page card that lets a signed-in user attach a passkey to
 * their account, list existing passkeys, and remove them.
 *
 * Works against /api/auth/passkey/* — the same endpoints the iOS app
 * uses. Bearer token comes from `localStorage.deepnets_token`.
 */
const PasskeysSection: React.FC = () => {
  const [passkeys, setPasskeys] = useState<PasskeySummary[] | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const authedHeaders = useCallback((): Record<string, string> => {
    const token = localStorage.getItem('deepnets_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }, []);

  const loadPasskeys = useCallback(async () => {
    try {
      const res = await apiFetch(`${getApiHost()}/api/auth/passkey`, {
        headers: authedHeaders(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Failed to load passkeys (${res.status})`);
      }
      const data = await res.json();
      setPasskeys(data.passkeys || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load passkeys');
      setPasskeys([]);
    }
  }, [authedHeaders]);

  useEffect(() => {
    loadPasskeys();
  }, [loadPasskeys]);

  const handleAdd = async () => {
    setIsAdding(true);
    setError(null);
    setSuccess(null);
    try {
      // 1. Begin — server returns PublicKeyCredentialCreationOptions.
      const beginRes = await apiFetch(
        `${getApiHost()}/api/auth/passkey/register/begin`,
        {
          method: 'POST',
          headers: authedHeaders(),
          body: JSON.stringify({}),
        }
      );
      if (!beginRes.ok) {
        const err = await beginRes.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to start passkey registration');
      }
      const options = await beginRes.json();

      // 2. Run the WebAuthn ceremony via the platform authenticator
      //    (Touch ID, Windows Hello, Android device, security key).
      const credential = await startRegistration({ optionsJSON: options });

      // 3. Complete — server verifies attestation and appends the
      //    passkey to the user doc. Returns a refreshed JWT.
      const completeRes = await apiFetch(
        `${getApiHost()}/api/auth/passkey/register/complete`,
        {
          method: 'POST',
          headers: authedHeaders(),
          body: JSON.stringify({
            ...credential,
            passkeyName: describeBrowser(),
          }),
        }
      );
      if (!completeRes.ok) {
        const err = await completeRes.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to register passkey');
      }
      const data = await completeRes.json();
      if (data.token) {
        localStorage.setItem('deepnets_token', data.token);
      }
      setSuccess('Passkey added.');
      await loadPasskeys();
    } catch (err) {
      // User cancellation surfaces as a DOMException; show a clean message.
      const message = err instanceof Error ? err.message : 'Failed to add passkey';
      if (message.includes('cancel') || message.includes('timed out')) {
        setError('Cancelled.');
      } else {
        setError(message);
      }
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemove = async (credentialId: string) => {
    setRemovingId(credentialId);
    setError(null);
    setSuccess(null);
    try {
      const res = await apiFetch(
        `${getApiHost()}/api/auth/passkey/${encodeURIComponent(credentialId)}`,
        { method: 'DELETE', headers: authedHeaders() }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to remove passkey');
      }
      setSuccess('Passkey removed.');
      await loadPasskeys();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove passkey');
    } finally {
      setRemovingId(null);
    }
  };

  const isPlatformAuthenticatorAvailable = typeof window !== 'undefined'
    && 'credentials' in navigator
    && 'create' in navigator.credentials;

  return (
    <div className="bg-crypto-gray border border-gray-700 rounded-xl p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Passkeys</h2>
        {passkeys && (
          <span className="text-xs text-gray-400">
            {passkeys.length} registered
          </span>
        )}
      </div>

      <p className="text-sm text-gray-400 mb-4">
        Sign in without a wallet — Touch ID, Face ID, Windows Hello, or a
        security key. Syncs with your passkey manager (iCloud Keychain,
        1Password, etc.). Also used by the Deepnets iOS app.
      </p>

      {passkeys === null ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : passkeys.length === 0 ? (
        <div className="text-sm text-gray-500 mb-4">
          No passkeys yet. Add one to sign in faster next time.
        </div>
      ) : (
        <ul className="space-y-2 mb-4">
          {passkeys.map((p) => (
            <li
              key={p.credentialId}
              className="flex items-center justify-between bg-black/30 border border-gray-800 rounded-lg px-4 py-3"
            >
              <div className="min-w-0">
                <div className="text-white text-sm truncate">
                  {p.name || 'Passkey'}
                </div>
                <div className="text-xs text-gray-500">
                  Added {formatDate(p.createdAt)}
                  {p.lastUsedAt ? ` · Last used ${formatDate(p.lastUsedAt)}` : ''}
                </div>
              </div>
              <button
                onClick={() => handleRemove(p.credentialId)}
                disabled={removingId === p.credentialId}
                className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50 ml-4 shrink-0"
              >
                {removingId === p.credentialId ? 'Removing…' : 'Remove'}
              </button>
            </li>
          ))}
        </ul>
      )}

      <button
        onClick={handleAdd}
        disabled={isAdding || !isPlatformAuthenticatorAvailable}
        className="bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isAdding ? 'Follow prompts on your device…' : 'Add a passkey'}
      </button>

      {!isPlatformAuthenticatorAvailable && (
        <p className="text-xs text-yellow-500 mt-2">
          This browser doesn't expose WebAuthn. Try a recent Chrome, Safari,
          Firefox, or Edge.
        </p>
      )}

      {error && (
        <div className="mt-4 text-sm text-red-400">{error}</div>
      )}
      {success && (
        <div className="mt-4 text-sm text-green-400">{success}</div>
      )}
    </div>
  );
};

function describeBrowser(): string {
  if (typeof navigator === 'undefined') return 'Web';
  const ua = navigator.userAgent;
  const platform =
    /Mac/.test(ua) ? 'Mac' :
    /Windows/.test(ua) ? 'Windows' :
    /Android/.test(ua) ? 'Android' :
    /iPhone|iPad/.test(ua) ? 'iOS Safari' :
    /Linux/.test(ua) ? 'Linux' :
    'Web';
  const browser =
    /Edg\//.test(ua) ? 'Edge' :
    /Chrome\//.test(ua) ? 'Chrome' :
    /Firefox\//.test(ua) ? 'Firefox' :
    /Safari\//.test(ua) ? 'Safari' :
    'Browser';
  return `${browser} on ${platform}`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default PasskeysSection;
